// Moved from `src/hooks/mutation/useMutateDeleteTranscript.ts` (now a
// delegate) with the warm pair (ARCFE-991). Single-owned by the deletion
// family since the ARCFE-941 partition. Severed per the 0.5 worklist, with
// TWO success ports so each app reaction fires at its legacy position:
//   - `onTranscriptViewsRefresh` where `invalidateLiveTranscript` sat (the
//     capture-boundary 416L app implementation — the D2 ruling; it cannot
//     move, so the app supplies it).
//   - `onTranscriptDeleted` after the cache work, where toast + track sat.
// The t11n eviction is `removeQueries` ON PURPOSE — `structuralSharing`
// would resurrect the just-deleted recording on an invalidate-refetch. Do
// not tidy it into `invalidateQueries`.
import { useMutation } from '@tanstack/react-query';
import { isAxiosError } from 'axios';

import { logger } from '@heidi/runtime/logger';

import {
  GET_SESSION_BY_ID,
  TRANSCRIPTION_SERVICE_TRANSCRIPTS_QUERY_KEY,
} from '../../contracts';
import { deleteSessionTranscript } from '../api/transcription';
import { getSessionDataConfig } from '../config';
import { invalidateTranscriptBySessionId } from '../query/useQueryTranscriptBySessionId';

export const DELETE_TRANSCRIPT = 'deleteTranscript';

/**
 * Deletes a session's transcript via `DELETE /sessions/:id/transcript`, then
 * refreshes the transcript views and the session overview so the tab re-renders
 * empty. Gated at the call site behind `useIsTranscriptDeletionEnabled`.
 */
export function useMutateDeleteTranscript({
  onTranscriptViewsRefresh,
  onTranscriptDeleted,
  onDeleteError,
}: {
  /** App-reaction port: the live-transcript invalidation (capture-boundary). */
  onTranscriptViewsRefresh?: (sessionId: string) => void;
  /** App-reaction port: fired where the legacy toast + track fired. */
  onTranscriptDeleted?: (sessionId: string) => void;
  /** App-reaction port: fired where the legacy error toast fired. */
  onDeleteError?: (error: unknown) => void;
} = {}) {
  return useMutation({
    mutationKey: [DELETE_TRANSCRIPT],
    mutationFn: async ({ sessionId }: { sessionId: string }) => {
      return await deleteSessionTranscript(sessionId);
    },
    onSuccess: (_, { sessionId }) => {
      const queryClient = getSessionDataConfig().getQueryClient();

      // Fire-and-forget: the confirm dialog awaits this mutation, so awaiting
      // the refetches would keep the modal open past the DELETE response.
      onTranscriptViewsRefresh?.(sessionId);
      void invalidateTranscriptBySessionId(sessionId);

      // Remove rather than invalidate: the t11n query's `structuralSharing`
      // merge would otherwise resurrect the just-deleted recording on refetch.
      queryClient.removeQueries({
        queryKey: [TRANSCRIPTION_SERVICE_TRANSCRIPTS_QUERY_KEY, sessionId],
      });

      // Pick up the `transcript_deleted_at` marker that drives the tab's
      // empty state; nothing else in this flow refetches the session.
      void queryClient.invalidateQueries({
        queryKey: [GET_SESSION_BY_ID, { sessionId }],
      });

      onTranscriptDeleted?.(sessionId);
    },
    onError: (error) => {
      // Log a sanitised subset only — the raw Axios error serialises the
      // session-ID-bearing request URL (and possibly the response body) into
      // log sinks, which is PHI-adjacent.
      logger.error('Failed to delete transcript', {
        status: isAxiosError(error) ? error.response?.status : undefined,
        message: error instanceof Error ? error.message : String(error),
      });
      onDeleteError?.(error);
    },
  });
}
